<script setup>
import { computed, onMounted, reactive, ref, useSlots } from 'vue'
import SingleNote from './_singleNote.vue'
import NoteAttachmentInput from './_noteAttachmentInput.vue'
import { useNotificationStore } from '@/stores/NotificationStore.js'

const props = defineProps({
    alignment: {
        type: String,
        default: ''
    },
    leadId: {
        type: Number,
        default: null
    },
    // Overrides the lead note routes, e.g. for company notes: { index, store, update(noteId), destroy(noteId) }
    endpoints: {
        type: Object,
        default: null
    },
    notes: {
        type: [Array, Object],
        default: () => []
    }
})

const notificationStore = useNotificationStore()

// Mirrors the server's ATTACHMENT_RULES on LeadNote and LeadCompanyNote
const ATTACHMENT_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.mp3,.m4a,.wav,.ogg,.opus,.webm,.aac'
const slots = useSlots()

const isCreatingNote = ref(false)
const open = ref(false)
const working = ref(false)
const pendingDeleteNoteId = ref(null)
const loaded = ref(false)

const noteEditor = ref(null)
// Kept apart from currentlyEditingNote.attachment, which holds the stored attachment ({ url }) shown in the form
const replacementFile = ref(null)

const errors = reactive({
    content: null
})

const creatingNote = reactive({
    note: '',
    attachment: null,
    note_date: ''
})

const currentlyEditingNote = reactive({
    id: '',
    note: '',
    note_date: '',
    attachment: null,
    attachment_url: ''
})

const localnotes = ref([])

onMounted(() => {
    loadNotes()
})

const urls = computed(() => props.endpoints ?? {
    index: route('crm.lead.notes', props.leadId),
    store: route('crm.leads.note.create', props.leadId),
    update: (noteId) => route('crm.leads.note.update', noteId),
    destroy: (noteId) => route('crm.leads.note.delete', noteId)
})

const hasHeaderSlot = computed(() => !!(slots['header-left'] && slots['header-right']))
const hasImageSlot = computed(() => !!slots.image)
const hasBodySlot = computed(() => !!slots.body)
const alignmentClass = computed(() => (props.alignment === 'center' ? 'text-center' : ''))
const buttonState = computed(() => (working.value ? 'waiting' : ''))

// The picker works with Date objects, while the API sends and accepts 'Y-m-d H:i' in the user's timezone
const parseNoteDate = (value) => (value ? new Date(value.replace(' ', 'T')) : null)

const formatNoteDate = (date) => {
    if (!(date instanceof Date)) {
        return ''
    }
    const pad = (n) => String(n).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const closeSlideout = () => {
    open.value = false
    replacementFile.value = null
}

const abortDelete = () => {
    pendingDeleteNoteId.value = null
}

const openInSlideout = (note) => {
    abortDelete()
    replacementFile.value = null
    Object.assign(currentlyEditingNote, {
        ...note,
        note_date: note.note_date || note.created_at?.user_tz || '',
        attachment: note.attachment
    })
    open.value = true
}

const startCreatingNote = () => {
    errors.content = null
    isCreatingNote.value = true
}

const resetCreateNoteForm = () => {
    creatingNote.note = ''
    creatingNote.attachment = null
    creatingNote.note_date = ''
    errors.content = null
    noteEditor.value?.resetEditor?.()
}

const loadNotes = async () => {
    const res = await axios.get(urls.value.index)
    localnotes.value = res.data.notes
    loaded.value = true
}

const notifyFailed = (error, fallback) => {
    notificationStore.addNotification({
        type: 'error',
        message: error?.response?.data?.message || fallback,
        dismissAfter: 5000
    })
}

const createNote = async () => {
    if (creatingNote.note) {
        working.value = true
        const formData = new FormData()
        if (creatingNote.attachment instanceof File) {
            formData.append('attachment', creatingNote.attachment)
        }
        formData.append('note', creatingNote.note)
        if (creatingNote.note_date) {
            formData.append('note_date', creatingNote.note_date)
        }

        try {
            const res = await axios.post(urls.value.store, formData)

            notificationStore.addNotification({
                type: res.data.notification.type,
                message: res.data.notification.message,
                dismissAfter: 2500
            })

            loadNotes()
            resetCreateNoteForm()
            isCreatingNote.value = false
        } catch (error) {
            notifyFailed(error, 'Failed to save note')
        } finally {
            working.value = false
        }
    } else {
        errors.content = _mft('crm:leadNoteCard.fieldMissing')
    }
}

const updateNote = async () => {
    working.value = true
    const formData = new FormData()
    formData.append('note', currentlyEditingNote.note)
    // A cleared date keeps the stored one rather than resetting it to now
    if (currentlyEditingNote.note_date) {
        formData.append('note_date', currentlyEditingNote.note_date)
    }

    if (replacementFile.value instanceof File) {
        formData.append('file', replacementFile.value)
    }

    try {
        const res = await axios.post(urls.value.update(currentlyEditingNote.id), formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'X-HTTP-Method-Override': 'PUT'
            }
        })

        notificationStore.addNotification({
            type: res.data.notification.type,
            message: res.data.notification.message,
            dismissAfter: 2500
        })

        if (res.data.notes) {
            localnotes.value = res.data.notes
        }
        closeSlideout()
        await loadNotes()
    } catch (error) {
        notifyFailed(error, 'Failed to save note')
    } finally {
        working.value = false
    }
}

const confirmDelete = async (noteId) => {
    working.value = true
    try {
        const res = await axios.delete(urls.value.destroy(noteId))

        notificationStore.addNotification({
            type: res.data.notification.type,
            message: res.data.notification.message,
            dismissAfter: 2500
        })

        currentlyEditingNote.id = null
        loadNotes()
        closeSlideout()
    } catch (error) {
        notifyFailed(error, 'Failed to delete note')
    } finally {
        working.value = false
    }
}

defineExpose({
    openInSlideout,
    createNote,
    updateNote,
    resetCreateNoteForm,
    closeSlideout,
    abortDelete,
    confirmDelete,
    isCreatingNote,
    open,
    working,
    localnotes,
    creatingNote,
    currentlyEditingNote,
    hasHeaderSlot,
    hasImageSlot,
    hasBodySlot,
    alignmentClass
})
</script>

<template>
    <div class="mb-8">
        <list-basic>
            <template #header>
                <div class="flex items-center justify-between gap-3">
                    <h3 class="mb-0 flex items-center gap-2">
                        {{ _mft('crm:leadNoteCard.domainNamePlural') }}
                        <span
                            v-if="loaded && localnotes.length > 0"
                            class="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600"
                        >
                            {{ localnotes.length }}
                        </span>
                    </h3>
                    <button-primary
                        size="sm"
                        @click="startCreatingNote"
                    >
                        <i class="fa-regular fa-plus mr-1"></i>
                        {{ _mft('crm:leadNoteCard.newNote') }}
                    </button-primary>
                </div>
            </template>

            <template v-if="!loaded">
                <li
                    v-for="n in 2"
                    :key="`skeleton-${n}`"
                    class="animate-pulse space-y-2 px-4 py-4"
                >
                    <div class="h-2.5 w-32 rounded bg-gray-200"></div>
                    <div class="h-3 w-full rounded bg-gray-100"></div>
                    <div class="h-3 w-2/3 rounded bg-gray-100"></div>
                </li>
            </template>

            <li
                v-else-if="localnotes.length === 0"
                class="px-4 py-10 text-center"
            >
                <div class="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                    <i class="fa-regular fa-note-sticky"></i>
                </div>
                <p class="text-sm font-medium text-gray-700">{{ _mft('crm:leadNoteCard.noNotesAdded') }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ _mft('crm:leadNoteCard.emptyStateDescription') }}</p>
                <button
                    type="button"
                    class="mf-link mt-3 text-sm font-medium"
                    @click="startCreatingNote"
                >
                    {{ _mft('crm:leadNoteCard.newNote') }}
                </button>
            </li>

            <template v-else>
                <li
                    v-for="note in localnotes"
                    :key="note.id"
                >
                    <SingleNote
                        :note="note"
                        @open-in-slideout="openInSlideout"
                    ></SingleNote>
                </li>
            </template>
        </list-basic>

        <slideout-with-slots
            v-if="open"
            @close="closeSlideout"
        >
            <template #title>
                <h1>{{ _mft('crm:leadNoteCard.editNote') }}</h1>
            </template>

            <template #body>
                <form @submit.prevent="updateNote">
                    <input-editor
                        :label="_mft('crm:leadNoteCard.domainNameSingular')"
                        :required="false"
                        :content="currentlyEditingNote.note"
                        name="note"
                        v-model:content="currentlyEditingNote.note"
                    />

                    <div class="mt-4">
                        <input-label :label="_mft('crm:leadNoteCard.noteDate')" />
                        <input-datepicker
                            :date="parseNoteDate(currentlyEditingNote.note_date)"
                            :show-inline="false"
                            :show-time="true"
                            :min-date="null"
                            text-input
                            class="mt-1"
                            @update:date="currentlyEditingNote.note_date = formatNoteDate($event)"
                        />
                    </div>

                    <div class="mt-4">
                        <input-label :label="_mft('crm:leadNoteCard.attachment')" />
                        <note-attachment-input
                            v-model="replacementFile"
                            :current-url="currentlyEditingNote.attachment?.url"
                            :accept="ATTACHMENT_ACCEPT"
                            class="mt-1"
                        />
                    </div>

                    <div class="mt-6 flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
                        <button-with-confirmation
                            style-type="danger"
                            confirm-type="danger"
                            modal-class="absolute bottom-full mb-2 -left-2 p-2 bg-white rounded shadow-md text-black z-modal"
                            :confirm-text="_mft('shared:confirm.areYouSure')"
                            :confirm-button-label="_mft('shared:action.delete')"
                            :abort-button-label="_mft('shared:action.cancel')"
                            @confirmed="confirmDelete(currentlyEditingNote.id)"
                        >
                            <i class="fa-regular fa-trash"></i>
                            <span class="sr-only">{{ _mft('shared:action.delete') }}</span>
                        </button-with-confirmation>

                        <div class="flex items-center gap-2">
                            <button-secondary @click="closeSlideout">
                                {{ _mft('shared:action.cancel') }}
                            </button-secondary>
                            <button-success
                                type="submit"
                                :state="buttonState"
                                :disabled="working"
                            >
                                {{ _mft('shared:action.update') }}
                            </button-success>
                        </div>
                    </div>
                </form>
            </template>
        </slideout-with-slots>

        <slideout-with-slots
            v-if="isCreatingNote"
            @close="isCreatingNote = false"
        >
            <template #title>
                <h1>{{ _mft('crm:leadNoteCard.newNote') }}</h1>
            </template>

            <template #body>
                <form @submit.prevent="createNote">
                    <input-editor
                        :label="_mft('crm:leadNoteCard.domainNameSingular')"
                        required="required"
                        :content="creatingNote.note"
                        name="note"
                        v-model:content="creatingNote.note"
                        ref="noteEditor"
                        autofocus
                        :error="errors.content"
                    />

                    <div class="mt-4">
                        <input-label :label="_mft('crm:leadNoteCard.noteDate')" />
                        <input-datepicker
                            :date="parseNoteDate(creatingNote.note_date)"
                            :show-inline="false"
                            :show-time="true"
                            :min-date="null"
                            text-input
                            class="mt-1"
                            @update:date="creatingNote.note_date = formatNoteDate($event)"
                        />
                        <p class="mt-1 text-xs text-gray-500">{{ _mft('crm:leadNoteCard.noteDateHelp') }}</p>
                    </div>

                    <div class="mt-4">
                        <input-label :label="_mft('crm:leadNoteCard.attachment')" />
                        <note-attachment-input
                            v-model="creatingNote.attachment"
                            :accept="ATTACHMENT_ACCEPT"
                            class="mt-1"
                        />
                    </div>

                    <div class="mt-6 flex items-center justify-end gap-2 border-t border-gray-100 pt-4">
                        <button-secondary @click="isCreatingNote = false">
                            {{ _mft('shared:action.cancel') }}
                        </button-secondary>
                        <button-success
                            type="submit"
                            :state="buttonState"
                            :disabled="working"
                        >
                            {{ _mft('crm:leadNoteCard.saveNote') }}
                        </button-success>
                    </div>
                </form>
            </template>
        </slideout-with-slots>
    </div>
</template>
